
import { annotationSourceReference, canAnnotateImage } from '@/utils/annotationSource';
import Vue, { PropType } from 'vue';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import {
	RunnitNodeField,
	IMAGE_UPLOAD_MODE,
	RunnitNodeRunInputValue,
	RunnitMask,
	RunnitNodeRunResult,
	RunnitUpload,
} from '@run-diffusion/shared';
import ImageUpload from '@/views/Runnits/RunnitSettings/ImageInput/ImageUpload.vue';
import RunnitSelectedImage from '@/views/Runnits/RunnitSelectedImage.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import { db, storage } from '@/firebase';
import InpaintingEditor from '@/views/Runnits/RunnitSettings/InpaintingEditor/InpaintingEditor.vue';
import VisualPromptEditor from '@/views/Runnits/RunnitSettings/VisualPromptEditor/VisualPromptEditor.vue';
import Pose3DEditor from '@/views/Runnits/RunnitSettings/Pose3DEditor/Pose3DEditor.vue';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import { IMAGE_GALLERY_DIALOG_NAV, ImageGalleryDialogNav } from '@/components/ImageGallery/utils';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import _debounce from 'lodash/debounce';
import ComplexBackgroundBaseStyledMenu from '@/components/designElements/ComplexBackgroundBaseStyledMenu.vue';
import { TeamUserMixin } from '@/mixins';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { fetchAsset3DAsFile } from '@/views/Runnits/RunnitSettings/Asset3D/asset3DFile';

export default Vue.extend({
	name: 'ImageInputField',
	props: {
		field: {
			type: Object as PropType<RunnitNodeField>,
			required: true,
		},
		value: {
			type: [String, Number, Object],
			required: false,
		},
		hasPromptField: {
			type: Boolean,
			default: false,
		},
	},
	data () {
		return {
			IMAGE_UPLOAD_MODE,
			IMAGE_GALLERY_DIALOG_NAV,
			maskUrl: null,
			debouncedStrength: null,
			debouncedStrengthInput: null as ((value: number) => void) | null,
			showUse3DSources: false,
		};
	},
	created () {
		this.debouncedStrengthInput = _debounce((value: number): void => {
			this.onInput(this.imageValue, this.inpaintingValue, value);
			this.debouncedStrength = null;
		}, 100);
	},
	destroyed () {
		this.exitSelectionMode(this.selectionStateTriggerId);
		if (this.debouncedStrengthInput) {
			this.debouncedStrengthInput.cancel();
		}
	},
	mixins: [
		RunnitBulkActionsMixin,
		RunnitsImageSelectMixin,
		TeamUserMixin,
	],
	watch: {
		inpaintingValue: {
			immediate: true,
			handler (newVal, oldVal) {
				if (newVal !== oldVal) {
					this.setMaskURL(this._get(newVal, 'mask.id') || null);
				}
			},
		},
		use3DSelectionStateForTrigger: {
			immediate: false,
			handler (newVal) {
				// The 3D picker never commits a field value: a chosen model is only a
				// source for the editor, and the capture is what becomes the image.
				const nodeRunResult = this._get(newVal, 'selectedNodeRunResults[0]') || null;
				const upload = this._get(newVal, 'selectedUploads[0]') || null;
				if (!nodeRunResult && !upload) return;
				this.openPose3DEditorFromLibrary({ nodeRunResult, upload });
			},
		},
		selectionStateForTrigger: {
			immediate: false,
			handler (newVal, oldVal) {
				this.commitSingleSelectionStateValue(
					newVal,
					oldVal,
					this.field.uuid,
					this._get(this.imageValue, 'nodeRunResult.uuid') || null,
					this._get(this.imageValue, 'upload.id') || null,
					() => this.onInput(null, null, null),
					(value) => this.onInput(value, this.inpaintingValue, this.strengthValue),
				);
			},
		},
	},
	computed: {
		canAnnotateSelectedImage (): boolean {
			return canAnnotateImage(this.imageValue?.nodeRunResult || this.imageValue?.upload);
		},
		...mapState(['runnitState']),
		...mapGetters(['getInpaintingEditorState']),
		getVisualPromptEditorState () {
			const states = this.runnitState.visualPromptEditorStates;
			return new Proxy(states, {
				get: (target, prop: string) => {
					return target[prop] || {
						open: false,
						saving: false,
						backgroundImageUrl: null,
						replaceImageTarget: null,
					};
				},
			});
		},
		getPose3DEditorState () {
			const states = this.runnitState.pose3DEditorStates;
			return new Proxy(states, {
				get: (target, prop: string) => {
					return target[prop] || {
						open: false,
						saving: false,
						assetFile: null,
						assetName: null,
					};
				},
			});
		},
		selectionStateTriggerId () {
			return `image-input-${this.field.uuid}`;
		},
		selectionStateForTrigger () {
			return this.getSelectionStateByTrigger(this.selectionStateTriggerId);
		},
		use3DSelectionStateTriggerId () {
			return `image-input-3d-${this.field.uuid}`;
		},
		use3DSelectionStateForTrigger () {
			return this.getSelectionStateByTrigger(this.use3DSelectionStateTriggerId);
		},
		rightPercentage () {
			if (this.field.display.strengthWholeNumber) {
				// 0-100 range: value is already a percentage
				return Math.round(this.strengthValue || 0);
			} else {
				// 0-1 range: convert to percentage
				return Math.round((this.strengthValue || 0) * 100);
			}
		},
		leftPercentage () {
			// Always 100 - right percentage to ensure they add up to 100%
			return 100 - this.rightPercentage;
		},
		leftIcon () {
			return this.field.display.strengthImageWeight ? 'mdi-text' : 'mdi-image-outline';
		},
		rightIcon () {
			return this.field.display.strengthImageWeight ? 'mdi-image-outline' : 'mdi-text';
		},
		leftLabel () {
			return this.field.display.strengthImageWeight ? 'Prompt Weight' : 'Image Weight';
		},
		rightLabel () {
			return this.field.display.strengthImageWeight ? 'Image Weight' : 'Prompt Weight';
		},
		isImageSelected () {
			return !!(
				this.imageValue &&
				(
					this.imageValue.nodeRunResult ||
					this.imageValue.upload
				)
			);
		},
		imageValue () {
			let val = this.value
			if (this.field.display.hasInpainting || this.field.display.hasStrength) {
				val = this._get(this.value, 'image') || null;
			}
			return val;
		},
		inpaintingValue () {
			let val = null;
			if (this.field.display.hasInpainting || this.field.display.hasStrength) {
				val = this._get(this.value, 'inpainting') || null;
			}
			return val;
		},
		strengthValue () {
			let val = null;
			if (this.field.display.hasInpainting || this.field.display.hasStrength) {
				val = this._get(this.value, 'strength') || (this.field.display.strengthWholeNumber ? 80 : 0.8);
			}
			return val;
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateInpaintingEditorState',
			'removeInpaintingEditorState',
			'updateVisualPromptEditorState',
			'removeVisualPromptEditorState',
			'updatePose3DEditorState',
			'updateSnackbar',
		]),
		openMenu () {
			this.menu = true;
		},
		triggerUpload (close: Function) {
			this.$refs.imageUpload.openFileDialog();
			close();
		},
		openLibrary (imageGalleryDialogInitialNav: ImageGalleryDialogNav, close: Function) {
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav: imageGalleryDialogInitialNav,
			});
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.SINGLE, this.selectionStateTriggerId, this.field, ['IMG']);
			close();
		},
		clearImage (close: Function) {
			this.$emit('input', null);
			close();
		},
		handleImageUpload (upload: any) {
			if (upload.nodeRunResult) {
				this.onInput({ nodeRunResult: upload.nodeRunResult }, this.inpaintingValue, this.strengthValue);
			} else if (upload.upload) {
				this.onInput({ upload: upload.upload }, this.inpaintingValue, this.strengthValue);
			} else {
				this.onInput({ upload }, this.inpaintingValue, this.strengthValue);
			}
		},
		onInput (image: RunnitNodeRunInputValue, inpainting: RunnitNodeRunInputValue, strength: number) {
			if (this.field.display.hasInpainting || this.field.display.hasStrength) {
				if (image || inpainting || strength) {
					this.$emit('input', { image, inpainting, strength });
				} else {
					this.$emit('input', null);
				}
			} else {
				this.$emit('input', image);
			}
		},
		forwardDragEvent (method, event) {
			if (this.$refs.imageUpload && typeof this.$refs.imageUpload[method] === 'function') {
				this.$refs.imageUpload[method](event);
			}
		},
		async handleInpaintingClick (close: Function) {
			const currentState = this.getInpaintingEditorState(this.field.uuid);
			if (currentState.saving) return;

			const imageUrl = await this.fetchImageUrl();

			// Close other menus globally
			this.updateRunnitState({
				promptGeneratorOpen: false,
				imageSelectOpen: false,
			});

			// Update field-specific state
			if (currentState.open) {
				this.removeInpaintingEditorState(this.field.uuid);
			} else {
				this.updateInpaintingEditorState({
					fieldUuid: this.field.uuid,
					editorState: {
						open: true,
						backgroundImageUrl: imageUrl,
					},
				});
			}
			close();
		},
		async handleVisualPromptClick (close: Function) {
			const currentState = this.getVisualPromptEditorState[this.field.uuid];
			if (currentState.saving || !this.canAnnotateSelectedImage) return;
			const baseSource = annotationSourceReference(this.imageValue.nodeRunResult || this.imageValue.upload, this.imageValue.nodeRunResult ? 'nodeRunResult' : 'upload');
			if (!baseSource) return;

			const imageUrl = await this.fetchImageUrl();

			// Close other menus globally
			this.updateRunnitState({
				promptGeneratorOpen: false,
				imageSelectOpen: false,
			});

			// Update field-specific state
			if (currentState.open) {
				this.removeVisualPromptEditorState(this.field.uuid);
			} else {
				this.updateVisualPromptEditorState({
					fieldUuid: this.field.uuid,
					editorState: {
						open: true,
						backgroundImageUrl: imageUrl,
						baseSource,
					},
				});
			}
			close();
		},
		async fetchImageUrl () {
			if (this.imageValue.nodeRunResult) {
				const { imageUrl } = await this.fetchSrc(this.imageValue.nodeRunResult, 'nodeRunResult');
				return imageUrl;
			}
			if (this.imageValue.upload) {
				const { imageUrl } = await this.fetchSrc(this.imageValue.upload, 'upload');
				return imageUrl;
			}
			return null;
		},
		onInpaintingMaskChange (mask) {
			this.setMaskURL(this._get(mask, 'id') || null);
			this.onInput(this.imageValue, { mask }, this.strengthValue);
		},
		onVisualPromptChange (newImageValue) {
			// The composited image replaces the original image value
			this.onInput(newImageValue, this.inpaintingValue, this.strengthValue);
		},
		onPose3DCapture (newImageValue) {
			// The rendered Pose3D screenshot replaces the original image value.
			this.onInput(newImageValue, this.inpaintingValue, this.strengthValue);
		},
		toggleUse3DSources () {
			this.showUse3DSources = !this.showUse3DSources;
		},
		triggerUse3DUpload (close: Function) {
			const input = this.$refs.pose3dFileInput as HTMLInputElement | undefined;
			if (input) input.click();
			this.showUse3DSources = false;
			close();
		},
		openUse3DLibrary (close: Function) {
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav: IMAGE_GALLERY_DIALOG_NAV.GENERATIONS,
			});
			// A separate trigger id from the image picker so a chosen model opens the
			// editor instead of being committed as this field's image value.
			this.enterSelectionMode(
				NODE_RUN_RESULT_SELECTION_MODE.SINGLE,
				this.use3DSelectionStateTriggerId,
				this.field,
				['ASSET_3D'],
			);
			this.showUse3DSources = false;
			close();
		},
		async openPose3DEditorFromLibrary (source: { nodeRunResult?: any, upload?: any }) {
			try {
				const file: File = await fetchAsset3DAsFile(source);
				this.updatePose3DEditorState({
					fieldUuid: this.field.uuid,
					editorState: {
						open: true,
						saving: false,
						assetFile: file,
						assetName: file.name,
					},
				});
			} catch (err: any) {
				console.error('Failed to open 3D asset from library', err);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: err && err.message ? err.message : 'Could not open that 3D asset',
					show: true,
				});
			} finally {
				this.clearSelectionsFromState(this.use3DSelectionStateTriggerId);
				this.exitSelectionMode(this.use3DSelectionStateTriggerId);
			}
		},
		onPose3DFileSelected (ev: Event) {
			const target = ev.target as HTMLInputElement;
			const file = target.files && target.files[0];
			target.value = '';
			if (!file) return;
			this.updatePose3DEditorState({
				fieldUuid: this.field.uuid,
				editorState: {
					open: true,
					saving: false,
					assetFile: file,
					assetName: file.name,
				},
			});
		},
		async setMaskURL (maskId) {
			if (maskId) {
				const maskRef = db.doc(`runnitMasks/${maskId}`);
				const mask: RunnitMask = (await maskRef.get()).data() as RunnitMask;

				const maskImageRef = storage.ref(`runnitMasks/users/${mask.userId}/masks/${mask.name}`);

				let maskURL = null;
				try {
					// Exists
					maskURL = await maskImageRef.getDownloadURL();
				} catch (err) {
					// doesn't exist
				}
				this.maskUrl = maskURL;
			} else {
				this.maskUrl = null;
			}
		},
		handleStrengthInput (value: number): void {
			this.debouncedStrength = value;
			if (this.debouncedStrengthInput) {
				this.debouncedStrengthInput(value);
			}
		},
		generatePromptFromImage (close: Function) {
			// Emit an event to the parent component to trigger prompt generation
			this.$emit('generate-prompt-from-image', {
				nodeRunResult: this.imageValue?.nodeRunResult,
				upload: this.imageValue?.upload,
			});
			close();
		},
	},
	components: {
		MenuOrBottomSheet,
		ImageUpload,
		RunnitSelectedImage,
		InpaintingEditor,
		VisualPromptEditor,
		Pose3DEditor,
		ComplexBackgroundBaseStyledMenu,
	},
});
